import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom'; // Import Outlet from react-router-dom
import { useSelector } from 'react-redux';
import { selectTheme, selectSidebarCollapsed } from '../slices/preferencesSlice';
import { selectAuthIsForecaster } from '../slices/authSlice';
import MobileTabBar from './MobileTabBar';
import MobileMoreSheet from './MobileMoreSheet';
import { BreadcrumbProvider } from '../contexts/BreadcrumbContext';
import { MarketPickerProvider } from '../contexts/MarketPickerContext';
import Sidebar from './Sidebar';
import Header from './Header';
import PlaygroundBanner from './PlaygroundBanner';
import ResourceUrlSync from './ResourceUrlSync';
import LegalUpdateDialog from './LegalUpdateDialog';

const isPlayground = process.env.REACT_APP_ENVIRONMENT === 'playground';

export default function Layout({ navigation, teams, userNavigation }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const theme = useSelector(selectTheme);
  const collapsed = useSelector(selectSidebarCollapsed);
  const isForecaster = useSelector(selectAuthIsForecaster);
  const location = useLocation();

  useEffect(() => {
    const root = document.documentElement;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const applyTheme = (t) => {
      const effectiveDark = t === 'dark' || (t === 'system' && mediaQuery.matches);
      if (effectiveDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    };

    applyTheme(theme);

    const listener = (e) => {
      if (theme === 'system') {
        if (e.matches) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    };

    mediaQuery.addEventListener ? mediaQuery.addEventListener('change', listener) : mediaQuery.addListener(listener);
    return () => {
      mediaQuery.removeEventListener ? mediaQuery.removeEventListener('change', listener) : mediaQuery.removeListener(listener);
    };
  }, [theme]);
  
  return (
    <BreadcrumbProvider>
      <MarketPickerProvider>
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:rounded-md focus:bg-brand-600 focus:px-3 focus:py-2 focus:text-sm focus:text-white"
        >
          Skip to main content
        </a>
        <ResourceUrlSync />
        <PlaygroundBanner fixed />
        {/* Forecasters accept updated Terms of Use / Privacy Policy here. */}
        <LegalUpdateDialog />

        <div
          className="min-h-screen bg-surface text-gray-900 dark:bg-gray-870 dark:text-gray-100"
          style={isPlayground ? { paddingTop: 'var(--playground-banner-height, 0px)' } : undefined}
        >
          {/* Sidebar component */}
          <Sidebar navigation={navigation} teams={teams} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

          {/* Main content */}
          {/* Bottom padding clears the fixed MobileTabBar plus the iOS home
              indicator (safe-area inset), so the last page element never
              sits under the bar. */}
          <div className={`flex flex-col transition-all duration-300 ${collapsed ? 'lg:pl-16' : 'lg:pl-[224px]'} ${isForecaster ? 'pb-[calc(5rem+env(safe-area-inset-bottom))] lg:pb-0' : ''}`}>
            {/* Header */}
            <Header setSidebarOpen={setSidebarOpen} userNavigation={userNavigation} />

            {/* Main section with padding to accommodate sidebar */}
            <main id="main-content" className="flex-1">
              {/* lg:px-16 is the gutter the fixed 64px PerformancePaneHandle
                  sits in. Kept on 2xl: mx-auto only adds margin once the main
                  column passes 1600px (~1890px viewport with the sidebar open),
                  so a narrower 2xl gutter put the content's right edge (e.g.
                  the notices dismiss button) under the handle. */}
              <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto py-4 px-4 sm:px-6 lg:px-16">
                {/* `key` on pathname replays the fade-up on each route change */}
                <div key={location.pathname} className="view-enter">
                  {/* Use Outlet to render the nested routes */}
                  <Outlet/>
                </div>
              </div>
            </main>
          </div>

          {/* Forecaster phone navigation; renders null for other roles */}
          <MobileTabBar onOpenMenu={() => setMoreOpen(true)} />
          {/* Forecaster phone "More" sheet: destinations the tab bar does not
              carry. Non-forecasters keep the hamburger drawer (Sidebar above),
              since MobileTabBar (and its More button) only renders for them. */}
          {isForecaster && <MobileMoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />}
        </div>
      </MarketPickerProvider>
    </BreadcrumbProvider>
  );
}
